import React, { useContext, useEffect } from "react";
import { secured } from "react-abac";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Alert, Button, Grid, Icon, Link, Modal, Section, Spinner, Text } from "~/components-shared";
import { BarController, BarElement, CategoryScale, Chart as ChartJs, LinearScale, Tooltip } from "chart.js";
import { useTranslation } from "react-i18next";
import CoreContext from "~/lib/context";
import { useDispatch, useSelector } from "react-redux";
import { usePostHog } from "posthog-js/react";
import Markdown from "~/components/Markdown";
import { useHistory, useParams } from "react-router-dom";
import StyledIcon from "~/components/StyledIcon";
import styled from "styled-components";
import memberInsightsSelectors from "~/store/memberInsights/selectors";
import memberInsightsActionCreators from "~/store/memberInsights/actions";
import { MemberInsightsActionEnum } from "~/store/memberInsights/types";
import MemberInsightsInfoCard from "~/components/MemberInsightsInfoCard";
import MemberInsightsReportUpdateModal from "~/components/MemberInsightsReportUpdateModal";
import MemberInsightsReportDeleteModal from "~/components/MemberInsightsReportDeleteModal";
import useAbacExt from "~/hooks/abac";
import lists from "~/lib/lists";
import MemberInsightsWidgetsGrid from "~/components/MemberInsightsWidgetsGrid";
import MemberInsightsReportShareInSlackModal from "~/components/MemberInsightsReportShareInSlackModal";
ChartJs.register(BarController, CategoryScale, LinearScale, BarElement, Tooltip);
const HeadingText = styled(Text)`
  max-width: 740px;
`;
const MemberInsightsReport = () => {
  const posthog = usePostHog();
  const {
    t
  } = useTranslation();
  const params = useParams();
  const historyReports = useSelector(memberInsightsSelectors.getReportHistory(params.configId));
  const historyStatus = useSelector(memberInsightsSelectors.getReportHistoryStatus);
  const historyError = useSelector(memberInsightsSelectors.getReportHistoryError);
  const {
    configId
  } = params;
  const reportId = params.reportId || historyReports[0]?.id;
  const config = useSelector(memberInsightsSelectors.getConfig(configId));
  const configStatus = useSelector(memberInsightsSelectors.getStatusByConfig(configId));
  const configError = useSelector(memberInsightsSelectors.getErrorByConfig(configId));
  const report = useSelector(memberInsightsSelectors.getReport(reportId));
  const reportStatus = useSelector(memberInsightsSelectors.getStatusByReport(reportId));
  const reportError = useSelector(memberInsightsSelectors.getErrorByReport(reportId));
  const action = useSelector(memberInsightsSelectors.getAction);
  const generationStatus = report?.reportStatus?.status;
  const generationStatusMessage = report?.reportStatus?.message;
  const error = historyError || reportError || configError;
  const {
    setSidebar
  } = useContext(CoreContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    userHas
  } = useAbacExt();
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  useEffect(() => {
    if (historyStatus === StatusStateEnum.IDLE) {
      dispatch(memberInsightsActionCreators.fetchReportHistoryStarted(configId));
    }
  }, [historyStatus]);
  useEffect(() => {
    if (configStatus === StatusStateEnum.IDLE) {
      dispatch(memberInsightsActionCreators.fetchConfigStarted(configId));
    }
  }, [configStatus]);
  useEffect(() => {
    if (reportId && reportStatus === StatusStateEnum.IDLE) {
      dispatch(memberInsightsActionCreators.fetchReportStarted(configId, reportId));
    }
  }, [configId, reportId, reportStatus]);
  const handleBack = () => {
    history.push(RoutesEnum.MEMBER_INSIGHTS_CONFIGS);
    posthog.capture(`Member Insights Report – Back`, {
      configId,
      reportId
    });
  };
  if (!config && error || !report && error) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
      element: "h1",
      s: {
        mt: 5,
        mb: 8
      },
      className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
    }, "Report failure"), /*#__PURE__*/React.createElement(Grid, {
      cols: 1,
      justify: "center",
      s: {
        mt: 16
      }
    }, /*#__PURE__*/React.createElement(StyledIcon, {
      src: "warningOutline",
      color: "translucentDanger"
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      weight: "semiBold",
      display: "block",
      s: {
        mt: 6
      },
      align: "center",
      style: {
        maxWidth: "400px"
      }
    }, t("MemberInsightsReport.reportIssues")), /*#__PURE__*/React.createElement(Markdown, {
      color: "textSecondary",
      fontSize: 4,
      display: "block",
      s: {
        mt: 3,
        mb: 5
      },
      lineHeight: 6,
      style: {
        maxWidth: "400px"
      },
      align: "center",
      text: t("MemberInsightsReport.reportIssuesInfo")
    })));
  }
  if (!config) {
    return /*#__PURE__*/React.createElement(Spinner, {
      center: true,
      s: {
        my: 5
      }
    });
  }
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Link, {
    onClick: handleBack,
    weight: "semiBold",
    color: "text",
    s: {
      mt: 0,
      ml: -3
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    rotate: -90,
    s: {
      mt: -0.5
    }
  }), "Reports"), /*#__PURE__*/React.createElement(HeadingText, {
    element: "h1",
    s: {
      mb: 5
    },
    className: `${AnimOrderEnum.TWO} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, config.name), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: error || "",
    linkColor: "text"
  })), generationStatusMessage && /*#__PURE__*/React.createElement(Alert, {
    severity: reportStatus ? lists.insights.alert.statusToSeverity[reportStatus] : "warning",
    s: {
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr auto"
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: generationStatusMessage,
    linkColor: "text"
  }), reportStatus === StatusStateEnum.FAILED && /*#__PURE__*/React.createElement(Button
  // onClick={() => handleUpdate()}
  , {
    loading: generationStatus === StatusStateEnum.LOADING,
    color: "translucent",
    fontSize: 3,
    s: {
      px: 2,
      py: 1
    },
    display: "inline-block"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "refresh",
    scale: 4,
    s: {
      mr: 1
    }
  }), "Try again"))), /*#__PURE__*/React.createElement(MemberInsightsInfoCard, {
    s: {
      p: 0,
      mb: 5
    },
    config: config,
    report: report
  }), reportStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
    s: {
      my: 6
    },
    center: true
  }) : report ? /*#__PURE__*/React.createElement(MemberInsightsWidgetsGrid, {
    widgets: report.widgets.sort((a, b) => a.order - b.order),
    dateFrom: report.dateFrom,
    dateTo: report.dateTo,
    memberIds: config.members.map(member => member.id),
    view: config.config.map(item => ({
      ...item,
      enabled: true
    }))
  }) : /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    justify: "center",
    s: {
      mt: 16
    }
  }, /*#__PURE__*/React.createElement(StyledIcon, {
    src: "timelapse"
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "semiBold",
    display: "block",
    s: {
      mt: 5
    }
  }, t("MemberInsightsReport.noData")), /*#__PURE__*/React.createElement(Markdown, {
    color: "textSecondary",
    fontSize: 4,
    display: "block",
    s: {
      mt: 3,
      mb: 5
    },
    lineHeight: 6,
    style: {
      maxWidth: "500px"
    },
    align: "center",
    text: t("MemberInsightsReport.noDataInfo")
  })), userHas(AuthoritiesEnum.MEMBER_INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(MemberInsightsReportUpdateModal, null)), userHas(AuthoritiesEnum.MEMBER_INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(MemberInsightsReportDeleteModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === MemberInsightsActionEnum.SHARE_REPORT_IN_SLACK
  }, /*#__PURE__*/React.createElement(MemberInsightsReportShareInSlackModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.MEMBER_INSIGHTS_WRITE,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(MemberInsightsReport), () => true);